import React, { useEffect, useState, useRef } from "react";
import "./Converter.css";
import Nav3 from '../../../Navs/SideView/Nav3';
import { Helmet } from "react-helmet";
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../../../firebase';
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import styled from "styled-components";
import Modal from "./Modal";
import ReactPlayer from 'react-player';
import screenfull from 'screenfull';
import { BiFullscreen, BiExitFullscreen } from 'react-icons/bi';
import { MdPictureInPicture, MdPictureInPictureAlt } from 'react-icons/md';
import { FaShare, FaHeart, FaRegHeart, FaHistory, FaInfoCircle, FaExternalLinkAlt } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import ViewCounter from '../../../ViewCounter';

const Container = styled.div`
  background-color: #030617;
  background-image: linear-gradient(to bottom, #030617, #0a1128);
  min-height: 100vh;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: linear-gradient(to bottom, rgba(75, 192, 192, 0.05), transparent);
    z-index: 0;
  }
`;

const CardContainer = styled(motion.div)`
  width: 100%;
  max-width: 900px;
  margin: 20px auto;
  padding: 30px;
  background: linear-gradient(145deg, #1c2237 0%, #151a2b 100%);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  position: relative;
  z-index: 1;
  
  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, rgba(75, 192, 192, 0.1), transparent, rgba(75, 192, 192, 0.1));
    border-radius: 22px;
    z-index: -1;
  }
`;

const ChannelInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 16px;
  backdrop-filter: blur(10px);

  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }
`;

const ChannelImage = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 50%;
    background: linear-gradient(45deg, rgba(75, 192, 192, 0.2), rgba(75, 192, 192, 0));
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.5;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const ChannelLogo = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid rgba(75, 192, 192, 0.3);
`;

const ChannelDetails = styled.div`
  flex: 1;
`;

const ChannelName = styled.h1`
  font-size: 2rem;
  color: #fff;
  margin-bottom: 10px;
  font-weight: 600;
  
  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
`;

const LiveBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background: rgba(75, 192, 192, 0.1);
  color: rgb(75, 192, 192);
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.9rem;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    background: rgb(75, 192, 192);
    border-radius: 50%;
    margin-right: 8px;
    animation: blink 1.5s infinite;
  }

  @keyframes blink {
    0% { opacity: 1; }
    50% { opacity: 0.4; }
    100% { opacity: 1; }
  }
`;

const PlayerWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);

  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const StyledReactPlayer = styled(ReactPlayer)`
  position: absolute;
  top: 0;
  left: 0;
`;

const Controls = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 10px;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${PlayerWrapper}:hover & {
    opacity: 1;
  }
`;

const ControlButton = styled.button`
  background: rgba(0, 0, 0, 0.7);
  border: none;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(75, 192, 192, 0.8);
    transform: scale(1.1);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const AdContainer = styled.div`
  width: 100%;
  max-width: 728px;
  margin: 20px 0;
  display: flex;
  justify-content: center;
  position: relative;
  z-index: 1;
`;

// New styled components for enhanced UI
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  color: white;
`;

const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 5px solid rgba(75, 192, 192, 0.1);
  border-top-color: rgb(75, 192, 192);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 20px;
  padding: 15px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
  padding: 10px;
  border-radius: 8px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: rgb(75, 192, 192);
  }
  
  svg {
    margin-bottom: 5px;
    font-size: 20px;
  }
`;

const ChannelDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1.6;
  margin-top: 10px;
  margin-bottom: 20px;
`;

const ShareModal = styled(motion.div)`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #1c2237;
  padding: 20px;
  border-radius: 10px;
  z-index: 1000;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
`;

const ShareModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 999;
  backdrop-filter: blur(3px);
`;

const ShareButton = styled.button`
  background: rgba(75, 192, 192, 0.1);
  color: rgb(75, 192, 192);
  border: 1px solid rgba(75, 192, 192, 0.3);
  border-radius: 8px;
  padding: 10px 15px;
  margin: 10px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(75, 192, 192, 0.2);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
`;

const RecentlyPlayed = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;

const RecentlyPlayedTitle = styled.h3`
  color: white;
  font-size: 18px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 10px;
  }
`;

const RecentlyPlayedList = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 15px;
  padding-bottom: 10px;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(75, 192, 192, 0.3);
    border-radius: 4px;
  }
`;

const RecentlyPlayedItem = styled.div`
  flex: 0 0 auto;
  width: 80px;
  text-align: center;
  cursor: pointer;
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-5px);
  }
  
  img {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid rgba(75, 192, 192, 0.3);
  }
  
  p {
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
    margin-top: 5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

function TVPlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tv, setTv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPiP, setIsPiP] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const playerRef = useRef(null);
  const wrapperRef = useRef(null);
  const videoRef = useRef(null);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const mobileKeywords = ['android', 'webos', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone'];
      return mobileKeywords.some(keyword => userAgent.includes(keyword));
    };

    setIsMobile(checkMobile());
  }, []);

  // Handle visibility change for mobile PiP
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!isMobile || !document.pictureInPictureEnabled) return;

      try {
        if (document.visibilityState === 'hidden') {
          const videoElement = videoRef.current || 
                             playerRef.current?.getInternalPlayer() || 
                             wrapperRef.current?.querySelector('video');

          if (videoElement && !document.pictureInPictureElement) {
            await videoElement.requestPictureInPicture();
          }
        } else if (document.pictureInPictureElement) {
          await document.exitPictureInPicture();
        }
      } catch (error) {
        console.error('Failed to handle PiP on visibility change:', error);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isMobile]);

  // Handle scroll for mobile PiP
  useEffect(() => {
    let scrollTimeout;
    const handleScroll = async () => {
      if (!isMobile || !document.pictureInPictureEnabled) return;

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(async () => {
        try {
          const videoElement = videoRef.current || 
                             playerRef.current?.getInternalPlayer() || 
                             wrapperRef.current?.querySelector('video');
          
          if (!videoElement) return;

          const rect = videoElement.getBoundingClientRect();
          const isVideoVisible = rect.top >= 0 && 
                               rect.bottom <= window.innerHeight;

          if (!isVideoVisible && !document.pictureInPictureElement) {
            await videoElement.requestPictureInPicture();
          } else if (isVideoVisible && document.pictureInPictureElement) {
            await document.exitPictureInPicture();
          }
        } catch (error) {
          console.error('Failed to handle PiP on scroll:', error);
        }
      }, 150); // Debounce scroll events
    };

    if (isMobile) {
      window.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (isMobile) {
        window.removeEventListener('scroll', handleScroll);
        clearTimeout(scrollTimeout);
      }
    };
  }, [isMobile]);

  useEffect(() => {
    const fetchTv = async () => {
      setLoading(true);
      try {
        const tvRef = doc(db, "channels", id);
        const docSnap = await getDoc(tvRef);
        
        if (docSnap.exists()) {
          const tvData = docSnap.data();
          setTv(tvData);
          
          // Update view count
          try {
            await updateDoc(tvRef, {
              viewCount: increment(1)
            });
          } catch (updateError) {
            console.error("Error updating view count:", updateError);
          }
          
          // Add to recently viewed
          const recentlyViewedChannels = JSON.parse(localStorage.getItem('recentlyViewedChannels') || '[]');
          
          // Check if this channel is already in recently viewed
          const existingIndex = recentlyViewedChannels.findIndex(channel => channel.id === id);
          if (existingIndex !== -1) {
            // Remove it so we can add it to the front
            recentlyViewedChannels.splice(existingIndex, 1);
          }
          
          // Add current channel to the front
          recentlyViewedChannels.unshift({
            id,
            title: tvData.title,
            image: tvData.image
          });
          
          // Keep only the last 5 channels
          const updatedRecent = recentlyViewedChannels.slice(0, 5);
          localStorage.setItem('recentlyViewedChannels', JSON.stringify(updatedRecent));
          setRecentlyViewed(updatedRecent);
          
          // Check if this channel is a favorite
          const favorites = JSON.parse(localStorage.getItem('favoriteChannels') || '[]');
          setIsFavorite(favorites.some(fav => fav.id === id));
          
        } else {
          setError("TV channel not found");
          console.log("No such document in Firestore");
        }
      } catch (error) {
        setError("Error loading TV channel");
        console.log("Error getting document:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTv();
    
    // Load recently viewed channels
    const recentlyViewedChannels = JSON.parse(localStorage.getItem('recentlyViewedChannels') || '[]');
    setRecentlyViewed(recentlyViewedChannels);

    const handleFullscreenChange = () => {
      setIsFullscreen(screenfull.isFullscreen);
    };

    const handlePiPChange = (event) => {
      setIsPiP(!!document.pictureInPictureElement);
    };

    if (screenfull.isEnabled) {
      screenfull.on('change', handleFullscreenChange);
    }

    document.addEventListener('enterpictureinpicture', handlePiPChange);
    document.addEventListener('leavepictureinpicture', handlePiPChange);

    return () => {
      if (screenfull.isEnabled) {
        screenfull.off('change', handleFullscreenChange);
      }
      document.removeEventListener('enterpictureinpicture', handlePiPChange);
      document.removeEventListener('leavepictureinpicture', handlePiPChange);
    };
  }, [id]);

  const handleFullScreen = () => {
    if (screenfull.isEnabled) {
      screenfull.toggle(wrapperRef.current);
    }
  };

  const handlePiP = async () => {
    try {
      if (!document.pictureInPictureEnabled) {
        console.log('Picture-in-Picture is not enabled');
        return;
      }

      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        // Get the video element from ReactPlayer
        const player = playerRef.current;
        if (!player) return;

        // Try different methods to get the video element
        let videoElement = null;

        // Method 1: Direct internal player
        if (player.getInternalPlayer()) {
          videoElement = player.getInternalPlayer();
        }

        // Method 2: Query selector
        if (!videoElement) {
          videoElement = wrapperRef.current.querySelector('video');
        }

        // Method 3: Stored ref
        if (!videoElement && videoRef.current) {
          videoElement = videoRef.current;
        }

        if (!videoElement) {
          console.log('Could not find video element');
          return;
        }

        await videoElement.requestPictureInPicture();
      }
    } catch (error) {
      console.error('Failed to toggle Picture-in-Picture mode:', error);
    }
  };

  const onReady = () => {
    // Store video element reference when player is ready
    const player = playerRef.current;
    if (player) {
      const video = player.getInternalPlayer();
      if (video) {
        videoRef.current = video;
      }
    }
  };
  
  // Toggle favorite status
  const toggleFavorite = () => {
    const favorites = JSON.parse(localStorage.getItem('favoriteChannels') || '[]');
    
    if (isFavorite) {
      // Remove from favorites
      const updatedFavorites = favorites.filter(fav => fav.id !== id);
      localStorage.setItem('favoriteChannels', JSON.stringify(updatedFavorites));
    } else {
      // Add to favorites
      favorites.push({
        id,
        title: tv.title,
        image: tv.image
      });
      localStorage.setItem('favoriteChannels', JSON.stringify(favorites));
    }
    
    setIsFavorite(!isFavorite);
  };
  
  // Share channel
  const shareChannel = async (platform) => {
    const url = window.location.href;
    const title = `Watch ${tv?.title} Live Stream on Konzo Vibez`;
    
    switch (platform) {
      case 'copy':
        try {
          await navigator.clipboard.writeText(url);
          alert('Link copied to clipboard!');
        } catch (err) {
          console.error('Failed to copy: ', err);
        }
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`);
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`);
        break;
      default:
        break;
    }
    
    setShowShareModal(false);
  };
  
  // Navigate to another channel
  const navigateToChannel = (channelId) => {
    navigate(`/tv/${channelId}`);
  };

  return (
    <>
      <Modal />
      <Nav3 />
      
      <AnimatePresence>
        {showShareModal && (
          <>
            <ShareModalOverlay 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowShareModal(false)}
            />
            <ShareModal
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', damping: 20 }}
            >
              <h3 style={{ color: 'white', marginBottom: '20px' }}>Share this channel</h3>
              <CloseButton onClick={() => setShowShareModal(false)}>×</CloseButton>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
                <ShareButton onClick={() => shareChannel('copy')}>Copy Link</ShareButton>
                <ShareButton onClick={() => shareChannel('whatsapp')}>WhatsApp</ShareButton>
                <ShareButton onClick={() => shareChannel('facebook')}>Facebook</ShareButton>
                <ShareButton onClick={() => shareChannel('twitter')}>Twitter</ShareButton>
              </div>
            </ShareModal>
          </>
        )}
      </AnimatePresence>
      
      <Container>
        {loading ? (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>Loading TV channel...</LoadingText>
          </LoadingContainer>
        ) : error ? (
          <LoadingContainer>
            <h2 style={{ color: 'white', marginBottom: '20px' }}>Error</h2>
            <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{error}</p>
            <button 
              style={{ 
                marginTop: '20px', 
                background: 'rgba(75, 192, 192, 0.1)', 
                color: 'rgb(75, 192, 192)',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
              onClick={() => navigate('/channels')}
            >
              Back to TV Channels
            </button>
          </LoadingContainer>
        ) : tv && (
          <>
            <Helmet>
              <title>{`${tv.title || ''} Live Stream`}</title>
              <meta name="description" content={`Watch ${tv.title || 'TV Channel'} live stream - Best of Kasese entertainment`} />
              <meta property="og:title" content={`${tv.title || 'TV Channel'} Live Stream`} />
              <meta property="og:image" content={tv.image || ''} />
              <link rel="icon" href={tv.image || ''} />
            </Helmet>
            
            <CardContainer
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <ChannelInfo>
                <ChannelImage>
                  <ChannelLogo src={tv.image} alt={tv.title} />
                </ChannelImage>
                <ChannelDetails>
                  <ChannelName>{tv.title}</ChannelName>
                  <LiveBadge>LIVE</LiveBadge>
                  {tv.description && (
                    <ChannelDescription>{tv.description}</ChannelDescription>
                  )}
                </ChannelDetails>
              </ChannelInfo>

              <PlayerWrapper ref={wrapperRef}>
                <StyledReactPlayer
                  ref={playerRef}
                  url={tv.url}
                  controls
                  playing
                  width="100%"
                  height="100%"
                  pip={true}
                  onReady={onReady}
                  config={{
                    file: {
                      attributes: {
                        disablePictureInPicture: false,
                        controlsList: 'nodownload',
                      },
                    },
                  }}
                />
                <Controls>
                  {document.pictureInPictureEnabled && (
                    <ControlButton onClick={handlePiP} title="Picture in Picture">
                      {isPiP ? <MdPictureInPictureAlt /> : <MdPictureInPicture />}
                    </ControlButton>
                  )}
                  <ControlButton onClick={handleFullScreen} title="Toggle Fullscreen">
                    {isFullscreen ? <BiExitFullscreen /> : <BiFullscreen />}
                  </ControlButton>
                </Controls>
              </PlayerWrapper>
              
              <ActionButtons>
                <ActionButton onClick={toggleFavorite}>
                  {isFavorite ? <FaHeart color="rgb(75, 192, 192)" /> : <FaRegHeart />}
                  <span>{isFavorite ? 'Favorited' : 'Favorite'}</span>
                </ActionButton>
                <ActionButton onClick={() => setShowShareModal(true)}>
                  <FaShare />
                  <span>Share</span>
                </ActionButton>
                <ActionButton onClick={() => navigate('/channels')}>
                  <FaExternalLinkAlt />
                  <span>All Channels</span>
                </ActionButton>
                {tv.website && (
                  <ActionButton onClick={() => window.open(tv.website, '_blank')}>
                    <FaInfoCircle />
                    <span>Website</span>
                  </ActionButton>
                )}
              </ActionButtons>
              
              {recentlyViewed.length > 1 && (
                <RecentlyPlayed>
                  <RecentlyPlayedTitle>
                    <FaHistory /> Recently Viewed
                  </RecentlyPlayedTitle>
                  <RecentlyPlayedList>
                    {recentlyViewed
                      .filter(channel => channel.id !== id) // Don't show current channel
                      .map((channel, index) => (
                        <RecentlyPlayedItem 
                          key={index}
                          onClick={() => navigateToChannel(channel.id)}
                        >
                          <img src={channel.image} alt={channel.title} />
                          <p>{channel.title}</p>
                        </RecentlyPlayedItem>
                      ))}
                  </RecentlyPlayedList>
                </RecentlyPlayed>
              )}
            </CardContainer>

            <AdContainer>
              <ins className="adsbygoogle"
                style={{ display: "block" }}
                data-ad-client="ca-pub-2968429081843062"
                data-ad-slot="8703718386"
                data-ad-format="auto"
                data-full-width-responsive="true"></ins>
            </AdContainer>

            <AdContainer>
              <ins className="adsbygoogle"
                style={{ display: "block" }}
                data-ad-client="ca-pub-2968429081843062"
                data-ad-slot="8456694735"
                data-ad-format="auto"
                data-full-width-responsive="true"></ins>
            </AdContainer>
            <ViewCounter path={`/tv/${id}`} />
          </>
        )}
      </Container>
    </>
  );
}

export default TVPlayer;
